import { lazy, Suspense } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import Main from '../components/Main';
import {
  CAN_ACCESS_ASSETS,
  CAN_ACCESS_HR,
  CAN_ACCESS_PAYROLL,
  CAN_ACCESS_SYSDEV,
  CAN_ACCESS_TICKETING_ADMIN,
  SUPER_ADMINS,
} from './roles';


// Custom loading component for better user experience
const LoadingFallback = () => (
  <div className="flex items-center justify-center w-full h-full min-h-[200px]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
  </div>
);

// Roles live in ./roles.js — the shared access lists imported above.
const POSITIONS = {
  SALES_MANAGER: 'Sales Manager',
  MANILA_BRANCH_MANAGER: 'Manila - Branch Manager',
  SOUTHERN_REGION_MANAGER: 'Southern Region Manager',
  CEBU_BRANCH_MANAGER: 'Cebu - Branch Manager',
  DIGITAL_MARKETING: 'Digital Marketing Coordinator',
  IT_DEV_MANAGER: 'IT Development Manager',
  FINANCE_ADMIN_MANAGER: 'Finance & Admin Manager/CFO',
};

// Helper function to wrap components with Suspense
const withSuspense = (Component) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

// Lazy loaded components - Authentication and Common
const Login = lazy(() => import('../pages/User/Login'));
const Register = lazy(() => import('../pages/User/Register'));
const Unauthorized = lazy(() => import('../pages/User/Unauthorized'));
/* const NotFound = lazy(() => import('../pages/User/NotFound')); */

// Lazy loaded components - User Dashboard
const Dashboard = lazy(() => import('../pages/User/Dashboard'));
const Profile = lazy(() => import('../pages/User/Profile'));
const Announcements = lazy(() => import('../pages/User/Announcements'));
const Attendance = lazy(() => import('../pages/User/Attendance'));
const ToDo = lazy(() => import('../pages/To-Do/ToDo.jsx'));
const Settings = lazy(() => import('../pages/User/Settings'));
const Resources = lazy(() => import('../pages/User/Resources'));
const About = lazy(() => import('../pages/User/About'));
const Home = lazy(() => import('../pages/User/Home'));
const NotificationsPage = lazy(() => import('../pages/User/notifications'));
const NotificationDetailsPage = lazy(() => import('../components/NotificationDetailPage.jsx'));

// Lazy loaded components - Department
const Department = lazy(() => import('../pages/Department/Department.jsx'));

// Lazy loaded components - Asset Inventory
const AssetsList = lazy(() => import('../pages/Asset Inventory/assetsList'));
const AssetDashboard = lazy(() => import('../pages/Asset Inventory/assetDashboard'));
const AssetHistory = lazy(() => import('../pages/Asset Inventory/assetHistory'));
const DisposedAssets = lazy(() => import('../pages/Asset Inventory/disposedAssets'));
const Requests = lazy(() => import('../pages/Asset Inventory/Requests.jsx'));
const AssetReviewPage = lazy(() => import('../pages/Asset Inventory/assetReview'));
const AssetReports = lazy(() => import('../pages/Asset Inventory/assetReports'));
const MigrateLocations = lazy(() => import('../pages/Asset Inventory/migrateLocations.jsx'));

// Lazy loaded components - Payroll Management
const PayrollDashboard = lazy(() => import('../pages/Payroll Management/payrollDashboard'));
const PayrollEmployees = lazy(() => import('../pages/Payroll Management/payrollEmployees'));
const Payroll = lazy(() => import('../pages/Payroll Management/Payroll'));
const PayrollApprovals = lazy(() => import('../pages/Payroll Management/payrollApprovals'));
const PayrollLoans = lazy(() => import('../pages/Payroll Management/payrollLoans'));

// Lazy loaded components - HR Management
const HRPanel = lazy(() => import('../pages/User/HRPanel.jsx'));
const HRDashboard = lazy(() => import('../pages/HR/HR-Dashboard.jsx'));
const HREmployees = lazy (() => import ('../pages/HR/hrEmployees.jsx'))
const HROfficecSupplies = lazy (() => import('../pages/HR/hrOfficeSuppliesInventory.jsx'))
const HRCalendar = lazy (() => import('../pages/HR/hrCalendar.jsx'))
const HRAttendance = lazy(() => import('../pages/HR/hrAttendance.jsx'))
const HRTimecards = lazy(() => import('../pages/HR/hrTimecards.jsx'))
const HRRequests = lazy(() => import('../pages/HR/hrRequests.jsx'))
const UserProfileConfig = lazy(() => import('../pages/HR/userProfileConfig.jsx'))
// Lazy loaded components - System Development
const SystemDevDashboard = lazy(() => import('../pages/System Dev/sysdevDashboard.jsx'));
const SystemDevModules = lazy(() => import('../pages/System Dev/sysDevModules.jsx'))
const AssetInventoryModule = lazy(() => import('../pages/System Dev/modules/assetInventoryModule.jsx'))
const Forms = lazy(() => import('../pages/System Dev/sysDevForms.jsx'));
const MigrateNotifications = lazy(() => import('../pages/System Dev/migrateNotifications.jsx'));
const leaveFormConfig = lazy(() => import('../pages/System Dev/forms/leaveFormConfig.jsx'));
const assetAcquisitionFormConfig = lazy(() => import('../pages/System Dev/forms/assetAcquisitionFormConfig.jsx'));
const assetDispositionFormConfig = lazy(() => import('../pages/System Dev/forms/assetDispositionFormConfig.jsx'));
const PrivacyPolicy = lazy(() => import('../pages/User/Privacy-Policy.jsx'));

// Lazy loaded components - Ticketing System
const TicketingDashboard = lazy(() => import('../pages/Ticketing System/ticketingDashboard.jsx'));
const TicketingAdminDashboard = lazy(() => import('../pages/Ticketing System/adminDashboard.jsx'));
const MyTickets = lazy(() => import('../pages/Ticketing System/myTickets.jsx'));
const AssignedTickets = lazy(() => import('../pages/Ticketing System/assignedTickets.jsx'));
const AllTickets = lazy(() => import('../pages/Ticketing System/allTickets.jsx'));
const CreateTicket = lazy(() => import('../pages/Ticketing System/createTicket.jsx'));
const TicketDetails = lazy(() => import('../pages/Ticketing System/ticketDetails.jsx'));
const TicketingReports = lazy(() => import('../pages/Ticketing System/ticketingReports.jsx'));
// Public routes that don't require authentication
export const publicRoutes = [
  {
    path: '/',
    element: withSuspense(Login),
  },
  {
    path: '/login',
    element: withSuspense(Login),
  },
  {
    path: '/register',
    element: withSuspense(Register),
  },
];

// Protected routes grouped by module
export const protectedRoutes = [
  // ===== User Dashboard Routes =====
  {
    path: '/user/dashboard',
    element: withSuspense(Dashboard),
  },
  {
    path: '/user/profile',
    element: withSuspense(Profile),
  },
  {
    path: '/user/announcements',
    element: withSuspense(Announcements),
  },
  {
    path: '/user/attendance-monitoring',
    element: withSuspense(Attendance),
  },
  {
    path: '/user/todo',
    element: withSuspense(ToDo),
  },
  {
    // Tasks, Assigned by me, Reports
    path: '/user/todo/:view',
    element: withSuspense(ToDo),
  },
  {
    path: '/user/todo/list/:listId',
    element: withSuspense(ToDo),
  },
  {
    path: '/user/settings',
    element: withSuspense(Settings),
  },
  {
    path: '/user/resources',
    element: withSuspense(Resources),
  },
  {
    path: '/user/about',
    element: withSuspense(About),
  },
  {
    path: '/user/home',
    element: withSuspense(Home),
  },
  {
    path: '/user/notifications',
    element: withSuspense(NotificationsPage),
  },
  {
    path: '/user/notifications/:notificationId',
    element: withSuspense(NotificationDetailsPage),
  },
  // Legacy paths for backward compatibility
  {
    path: '/user-dashboard',
    element: withSuspense(Dashboard),
  },
  
  // ===== Department Routes =====
  {
    path: '/department/overview',
    element: withSuspense(Department),
    // Managers reach this by position; admins reach it by role (either passes).
    roles: SUPER_ADMINS,
    positions: [
      POSITIONS.SALES_MANAGER,
      POSITIONS.MANILA_BRANCH_MANAGER,
      POSITIONS.SOUTHERN_REGION_MANAGER,
      POSITIONS.CEBU_BRANCH_MANAGER,
      POSITIONS.DIGITAL_MARKETING,
      POSITIONS.IT_DEV_MANAGER,
      POSITIONS.FINANCE_ADMIN_MANAGER
    ]
  },
  // Legacy path for backward compatibility
  {
    path: '/department',
    element: withSuspense(Department),
    roles: SUPER_ADMINS,
    positions: [
      POSITIONS.SALES_MANAGER,
      POSITIONS.MANILA_BRANCH_MANAGER,
      POSITIONS.SOUTHERN_REGION_MANAGER,
      POSITIONS.CEBU_BRANCH_MANAGER,
      POSITIONS.DIGITAL_MARKETING,
      POSITIONS.IT_DEV_MANAGER,
      POSITIONS.FINANCE_ADMIN_MANAGER
    ]
  },

  // ===== Asset Inventory Routes =====
  {
    path: '/assets/list',
    element: withSuspense(AssetsList),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/assets/dashboard',
    element: withSuspense(AssetDashboard),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/assets/history',
    element: withSuspense(AssetHistory),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/assets/disposed-assets',
    element: withSuspense(DisposedAssets),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/assets/requests',
    element: withSuspense(Requests),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/assets/review',
    element: withSuspense(AssetReviewPage),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/assets/reports',
    element: withSuspense(AssetReports),
    roles: CAN_ACCESS_ASSETS,
  },
  // Legacy paths for backward compatibility
  {
    path: '/assets',
    element: withSuspense(AssetsList),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/assets/assetdashboard',
    element: withSuspense(AssetDashboard),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/assets/assetHistory',
    element: withSuspense(AssetHistory),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/assets/disposedAssets',
    element: withSuspense(DisposedAssets),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/asset-requests',
    element: withSuspense(Requests),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/asset-review',
    element: withSuspense(AssetReviewPage),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/asset-reports',
    element: withSuspense(AssetReports),
    roles: CAN_ACCESS_ASSETS,
  },
  {
    path: '/asset/migrate',
    element: withSuspense(MigrateLocations),
    roles: CAN_ACCESS_ASSETS,
  },
  
  // ===== Payroll System Routes =====
  {
    path: '/payroll/employees',
    element: withSuspense(PayrollEmployees),
    roles: CAN_ACCESS_PAYROLL,
  },
  {
    path: '/payroll/dashboard',
    element: withSuspense(PayrollDashboard),
    roles: CAN_ACCESS_PAYROLL,
  },
  {
    path: '/payroll/process',
    element: withSuspense(Payroll),
    roles: CAN_ACCESS_PAYROLL,
  },
  {
    path: '/payroll/approvals',
    element: withSuspense(PayrollApprovals),
    roles: CAN_ACCESS_PAYROLL,
  },
  {
    path: '/payroll/loans',
    element: withSuspense(PayrollLoans),
    roles: CAN_ACCESS_PAYROLL,
  },
  // Legacy paths for backward compatibility
  {
    path: '/payroll/payroll-employees',
    element: withSuspense(PayrollEmployees),
    roles: CAN_ACCESS_PAYROLL,
  },
  {
    path: '/payroll',
    element: withSuspense(Payroll),
    roles: CAN_ACCESS_PAYROLL,
  },
  {
    path: '/payroll/payroll-approvals',
    element: withSuspense(PayrollApprovals),
    roles: CAN_ACCESS_PAYROLL,
  },
  {
    path: '/payroll/payroll-loans',
    element: withSuspense(PayrollLoans),
    roles: CAN_ACCESS_PAYROLL,
  },
  
  // ===== HR Management Routes =====
  {
    path: '/hr/hr-panel',
    element: withSuspense(HRPanel),
    roles: CAN_ACCESS_HR,
  },
  {
    path: '/hr/hr-dashboard',
    element: withSuspense(HRDashboard),
    roles: CAN_ACCESS_HR,
  },
  
  {
    path: '/hr/employees',
    element: withSuspense(HREmployees),
    roles: CAN_ACCESS_HR,
  },  
  {
    path: '/hr/office-supplies-inventory',
    element: withSuspense(HROfficecSupplies),
    roles: CAN_ACCESS_HR,
  },
  {
    path: '/hr/attendance',
    element: withSuspense(HRAttendance),
    roles: CAN_ACCESS_HR,
  },
  {
    path: '/hr/timecards',
    element: withSuspense(HRTimecards),
    roles: CAN_ACCESS_HR,
  },
  {
    path: '/hr/calendar',
    element: withSuspense(HRCalendar),
    roles: CAN_ACCESS_HR,
  },
  {
    path: '/hr/requests',
    element: withSuspense(HRRequests)                      ,
    roles: CAN_ACCESS_HR,
  },
  // Legacy paths for backward compatibility
  {
    path: '/hrpanel',
    element: withSuspense(HRPanel),
    roles: CAN_ACCESS_HR,
  },
  {
    path: '/hr-dashboard',
    element: withSuspense(HRDashboard),
    roles: CAN_ACCESS_HR,
  },
    {
    path: '/user-profile-config',
    element: withSuspense(UserProfileConfig),
    roles: CAN_ACCESS_HR,
  },
  {
    path: '/user-profile-config/:id',
    element: withSuspense(UserProfileConfig),
    roles: CAN_ACCESS_HR,
  },

  // ===== System Dev Routes =====
  {
    path: '/sysdev/dashboard',
    element: withSuspense(SystemDevDashboard),
    roles: CAN_ACCESS_SYSDEV,
  },
  {
    path: '/sysdev/modules',
    element: withSuspense(SystemDevModules),
    roles: CAN_ACCESS_SYSDEV,
  },
  {
    path: '/sysdev/migrate-notifications',
    element: withSuspense(MigrateNotifications),
    roles: CAN_ACCESS_SYSDEV,
  },
  {
    path: '/sysdev/modules/asset-inventory',
    element: withSuspense(AssetInventoryModule),
    roles: CAN_ACCESS_SYSDEV,
  },
  // Legacy path for backward compatibility
  {
    path: '/sysdev-dashboard',
    element: withSuspense(SystemDevDashboard),
    roles: CAN_ACCESS_SYSDEV,
  },
  {
    path: '/sysdev/forms',
    element: withSuspense(Forms),
    roles: CAN_ACCESS_SYSDEV,
  },
  {
    path: '/sysdev/forms/leave-config-form',
    element: withSuspense(leaveFormConfig),
    roles: CAN_ACCESS_SYSDEV,
  },
  {
    path: '/sysdev/forms/asset-acquisition-config-form',
    element: withSuspense(assetAcquisitionFormConfig),
    roles: CAN_ACCESS_SYSDEV,
  },
  {
    path: '/sysdev/forms/asset-disposition-config-form',
    element: withSuspense(assetDispositionFormConfig),
    roles: CAN_ACCESS_SYSDEV,
  },

  {
    path: '/user/privacy-policy',
    element: withSuspense(PrivacyPolicy)

  },

  // ===== Ticketing System Routes =====
  // Admin-facing pages
  {
    path: '/ticketing/dashboard',
    element: withSuspense(TicketingDashboard),
    roles: CAN_ACCESS_TICKETING_ADMIN,
  },
  {
    path: '/ticketing/admin',
    element: withSuspense(TicketingAdminDashboard),
    roles: CAN_ACCESS_TICKETING_ADMIN,
  },
  {
    path: '/ticketing/all-tickets',
    element: withSuspense(AllTickets),
    roles: CAN_ACCESS_TICKETING_ADMIN,
  },
  {
    path: '/ticketing/assigned',
    element: withSuspense(AssignedTickets),
    roles: CAN_ACCESS_TICKETING_ADMIN,
  },
  {
    path: '/ticketing/reports',
    element: withSuspense(TicketingReports),
    roles: CAN_ACCESS_TICKETING_ADMIN,
  },
  // Available to any authenticated user
  {
    path: '/ticketing/my-tickets',
    element: withSuspense(MyTickets),
  },
  {
    path: '/ticketing/create',
    element: withSuspense(CreateTicket),
  },
  {
    path: '/ticketing/tickets/:ticketId',
    element: withSuspense(TicketDetails),
  },

];

// Helper function to create protected route elements
export const createProtectedRoute = (route) => ({
  path: route.path,
  element: (
    <ProtectedRoute 
      allowedRoles={route.roles} 
      allowedPositions={route.positions}
    >
      <Main>
        {route.element}
      </Main>
    </ProtectedRoute>
  ),
});